import { type FC, type ReactNode, useCallback, useMemo } from "react";
import { InfoToggletip } from "widgets";
import { type FileDefinitionType, isFilenameOfType } from "widgets/utils";

import type { BrowsingProjectFileFragment } from "@/modules/files/graphql/files-queries.gql.js";
import CheckboxField, { CheckboxReduxFormField } from "@/ui-modules/core/redux-form/CheckboxField";

import { simpleDropdownFieldValidation } from "../../../app-utils/form.js";
import { numberBasicValidation } from "../../../app-utils/validation.js";
import {
  type Contig,
  type EventResource,
  type InputNfParamInput,
  type InputNfParamValueInput,
  NfParamSubTypeEnum,
  NfParamTypeEnum,
  type NfParamTypeInput,
  type NfParamValue,
  type NfPlaybookParam,
  type Organism,
  type ProjectFile,
  type Region,
} from "../../../graphql.js";
import { selMessage } from "../../../messages/message.js";
import {
  getGroupedNearPathsBeforeLastUnderscore,
  isBamOrSamFile,
  isFastaFile,
  isFastQFile,
  isFileAccessible,
} from "../../../modules/files/utils/file.js";
import { FILE_BROWSE_TYPES } from "../../../settings/settings.js";
import { FilePickerReduxFormField, type IFilePickerFieldProps } from "../../core/redux-form/FilePickerFormField.js";
import InputField, { InputReduxFormField } from "../../core/redux-form/InputField.js";
import { RefGenomeReduxFormField } from "../../core/redux-form/ReactRefGenomeField.js";
import { RegionSelectorReduxFormField, validateRegionInputField } from "../../core/redux-form/RegionSelectorField.js";
import { FieldTitle } from "../FieldTitle.js";

export interface INfWorkflowParamInputFieldProps {
  workflowParam: NfPlaybookParam;
  selectedGenome?: Organism;
  horizontalLayout?: boolean;
}

const NfWorkflowParamInputField: FC<INfWorkflowParamInputFieldProps> = (props) => {
  const title = useMemo(() => {
    let labelIcon: ReactNode;
    let title: string;

    if (props.workflowParam.mandatory) {
      labelIcon = "*";
      title = selMessage({ id: "form.input.tooltip.mandatory" });
    }

    const hintIcon = props.workflowParam.description && <InfoToggletip content={props.workflowParam.description} />;
    return <FieldTitle label={props.workflowParam.label} title={title} labelIcon={labelIcon} rightElement={hintIcon} />;
  }, []);

  const textValidation = useCallback(
    (value: NfParamValue[]) => freeTextWorkflowParamValidation(value?.[0]?.textValue, props.workflowParam),
    [],
  );
  const numberValidation = useCallback(
    // we need to validate the numerical input as a text string
    (value: NfParamValue[]) =>
      numberWorkflowParamValidation(
        value?.[0]?.numberValue != null ? String(value?.[0]?.numberValue) : null,
        props.workflowParam,
      ),
    [],
  );
  const filePickerValidation = useCallback(
    (value: NfParamValue[]) =>
      filePickerWorkflowParamValidation(
        value?.map((v) => v.file),
        props.workflowParam,
      ),
    [props.workflowParam?.type.subType],
  );
  const refGenomeValidation = useCallback(
    (value: NfParamValue[]) => refGenomeWorkflowParamValidation(value, props.workflowParam),
    [],
  );
  const genomicRegionValidation = useCallback(
    (value: NfParamValue[]) => genomicRegionWorkflowParamValidation(value?.[0]?.region, props.workflowParam),
    [],
  );

  switch (props.workflowParam.type.type) {
    case NfParamTypeEnum.String:
      return (
        <InputReduxFormField
          horizontalLayout={props.horizontalLayout}
          key={props.workflowParam.identifier}
          name={props.workflowParam.identifier}
          label={title}
          componentClass="input"
          component={InputField}
          placeholder="Enter text..."
          //maxLength={maxLength}
          //disabled={disabledAttribute}
          validate={textValidation}
          tooltip={props.workflowParam.description}
          format={(value: NfParamValue[]) => value?.[0]?.textValue}
          parse={(value: string): NfParamValue[] => (value ? [createNfParamValue({ textValue: value })] : null)}
        />
      );
    case NfParamTypeEnum.Number:
      return (
        <InputReduxFormField
          horizontalLayout={props.horizontalLayout}
          key={props.workflowParam.identifier}
          name={props.workflowParam.identifier}
          label={title}
          componentClass="input"
          component={InputField}
          placeholder="Enter number..."
          type="number"
          //disabled={disabledAttribute}
          validate={numberValidation}
          tooltip={props.workflowParam.description}
          format={(value: NfParamValue[]) => value?.[0]?.numberValue}
          parse={(value: number): NfParamValue[] => (value ? [createNfParamValue({ numberValue: value })] : null)}
        />
      );
    case NfParamTypeEnum.Boolean:
      return (
        <div>
          <CheckboxReduxFormField
            horizontalLayout={props.horizontalLayout}
            key={props.workflowParam.identifier}
            name={props.workflowParam.identifier}
            label={props.horizontalLayout ? null : title}
            leftLabel={props.horizontalLayout ? title : null}
            tooltipText={props.workflowParam.description}
            component={CheckboxField}
            format={(value: NfParamValue[]) => value?.[0]?.booleanValue}
            parse={(value: boolean): NfParamValue[] =>
              value != undefined ? [createNfParamValue({ booleanValue: value })] : null
            }
          />
        </div>
      );
    case NfParamTypeEnum.File: {
      let selectableFileTypes: FILE_BROWSE_TYPES[] | undefined;
      let baseFilterHint: string | undefined;
      let isSelectableFilter: ((file: BrowsingProjectFileFragment) => boolean) | undefined;
      let fileDefinitionTypes: FileDefinitionType[] | undefined;

      if (
        props.workflowParam.type.subType === NfParamSubTypeEnum.Unaligned ||
        props.workflowParam.type.subType === NfParamSubTypeEnum.UnalignedPaired ||
        props.workflowParam.type.subType === NfParamSubTypeEnum.Aligned
      ) {
        const requireAlignedReadsInput = props.workflowParam.type.subType === NfParamSubTypeEnum.Aligned;
        selectableFileTypes = [FILE_BROWSE_TYPES.REGULAR_FILE];
        fileDefinitionTypes = requireAlignedReadsInput ? ["BAM", "SAM"] : ["FASTQ"];
        baseFilterHint = `Files of type ${fileDefinitionTypes.join(" ")} can be selected.`;
        isSelectableFilter = (file) => {
          const filename = file.fileName ?? file.fullPath;
          return requireAlignedReadsInput
            ? isBamOrSamFile({ ...file, fileName: filename })
            : isFastQFile({ ...file, fileName: filename });
        };
      } else if (
        props.workflowParam.type.subType === NfParamSubTypeEnum.File ||
        props.workflowParam.type.subType === NfParamSubTypeEnum.Folder
      ) {
        selectableFileTypes =
          props.workflowParam.type.subType === NfParamSubTypeEnum.File
            ? [FILE_BROWSE_TYPES.REGULAR_FILE]
            : [FILE_BROWSE_TYPES.DIRECTORY];
        baseFilterHint =
          props.workflowParam.type.subType === NfParamSubTypeEnum.File
            ? `Any file can be selected.`
            : `Any folder can be selected.`;
      }
      return (
        <FilePickerReduxFormField
          key={props.workflowParam.identifier}
          horizontalLayout={props.horizontalLayout}
          name={props.workflowParam.identifier}
          title={title}
          baseFilterHint={baseFilterHint}
          fileDefinitionTypes={fileDefinitionTypes}
          isSelectableFilter={isSelectableFilter}
          //exclusiveSelection={!attribute.multipleValues} // only used for file picker
          selectableFileTypes={selectableFileTypes}
          validate={filePickerValidation}
          tooltip={props.workflowParam.description}
          format={(values: NfParamValue[]) => values?.filter((value) => value.file).map((value) => value.file)}
          parse={(values: ProjectFile[]): NfParamValue[] => values?.map((value) => createNfParamValue({ file: value }))}
        />
      );
    }
    case NfParamTypeEnum.Genome: {
      const filePickerProps: IFilePickerFieldProps =
        props.workflowParam.type.subType === NfParamSubTypeEnum.Folder
          ? {
              selectableFileTypes: [FILE_BROWSE_TYPES.DIRECTORY],
              singleSelection: true,
            }
          : {
              selectableFileTypes: [FILE_BROWSE_TYPES.REGULAR_FILE],
              baseFilterHint: `Files ending with .fasta .fa  can be selected, including the compressed versions.`,
              fileDefinitionTypes: ["FASTA"],
              isSelectableFilter: (file) => {
                const filename = file.fileName ?? file.fullPath;
                return isFastaFile({ ...file, fileName: filename });
              },
              singleSelection: true,
            };
      return (
        <RefGenomeReduxFormField
          horizontalLayout={props.horizontalLayout}
          key={props.workflowParam.identifier}
          name={props.workflowParam.identifier}
          title={title}
          tooltip={props.workflowParam.description}
          validate={refGenomeValidation}
          filePickerProps={filePickerProps}
          onlyExplorerGenome={true}
          format={(values: NfParamValue[]) => {
            if (values?.[0]?.file) {
              return values?.filter((value) => value.file).map((value) => value.file);
            } else {
              return values?.[0]?.organism;
            }
          }}
          parse={(value: ProjectFile[] | Organism): NfParamValue[] => {
            if (Array.isArray(value)) {
              return value?.map((file) => createNfParamValue({ file: file }));
            } else {
              return value ? [createNfParamValue({ organism: value })] : null;
            }
          }}
        />
      );
    }

    case NfParamTypeEnum.Region:
      return (
        <RegionSelectorReduxFormField
          horizontalLayout={props.horizontalLayout}
          key={props.workflowParam.identifier}
          name={props.workflowParam.identifier}
          title={title}
          genomeId={props.selectedGenome?.id}
          tooltip={props.workflowParam.description}
          validate={genomicRegionValidation}
          format={(value: NfParamValue[]) => value?.[0]?.region}
          parse={(value: Region): NfParamValue[] => (value ? [createNfParamValue({ region: value })] : null)}
        />
      );
    case NfParamTypeEnum.OutputDir:
      return null; // always filtered out
    default:
      throw new Error("Unknown workflow type " + props.workflowParam.type.type);
  }
  return null;
};

export default NfWorkflowParamInputField;

export const freeTextWorkflowParamValidation = (value: string, workflowParam: NfPlaybookParam) => {
  if (!workflowParam.mandatory) {
    if (!value || value.trim().length === 0) {
      return undefined;
    }
  }

  if (!value || value.trim().length === 0) {
    return `Your input is required`;
  }
  return undefined;
};

export const numberWorkflowParamValidation = (value: string, workflowParam: NfPlaybookParam) => {
  const text: string = !value ? value : value.trim();
  return numberBasicValidation(text, workflowParam.mandatory);
};

export const filePickerWorkflowParamValidation = (value: ProjectFile[], workflowParam: NfPlaybookParam) => {
  if (!value?.length) {
    if (workflowParam.mandatory) {
      return `Your input is required`;
    }
  } else {
    if (workflowParam.type.subType === NfParamSubTypeEnum.UnalignedPaired) {
      const groupedPairedFiles = getGroupedNearPathsBeforeLastUnderscore(value);
      const allPairedEndFiles: boolean = groupedPairedFiles.every((mapping) => mapping.length === 2);
      if (!allPairedEndFiles) {
        return "Paired-end data is expected, please make sure your selection of files can be grouped by pair with pattern *_R?[12].*";
      }
    }
  }
  return undefined;
};

export const refGenomeWorkflowParamValidation = (values: NfParamValue[], workflowParam: NfPlaybookParam) => {
  if (workflowParam.mandatory) {
    if (!values?.find((value) => value.file || value.organism)) {
      return `Your input is required`;
    }
  }
  return undefined;
};

export const genomicRegionWorkflowParamValidation = (value: any, workflowParam: NfPlaybookParam) => {
  const region: Region = value;
  const contig: Contig = region?.contig;

  const contigSimpleValidation = simpleDropdownFieldValidation(contig);

  if (workflowParam.mandatory) {
    if (contigSimpleValidation) {
      return contigSimpleValidation;
    }
  }
  return validateRegionInputField(value);
};

export function createNfWorkflowParamValueFromEventInput(resource: EventResource): NfParamValue | undefined {
  if (resource.file) {
    if (isFileAccessible(resource.file)) {
      return createNfParamValue({ file: resource.file });
    }
  } else if (resource.organism) {
    if (!resource.organism.hidden) {
      return createNfParamValue({ organism: resource.organism });
    }
  } else if (resource.region) {
    return createNfParamValue({ region: resource.region });
  }
  return undefined;
}

export function extractNfWorkflowParamInputValues(
  workflowParam: NfPlaybookParam,
  value: NfParamValue[],
): InputNfParamInput {
  const nfParamInputValues: InputNfParamValueInput[] = [];
  switch (workflowParam.type.type) {
    case NfParamTypeEnum.String: {
      value?.forEach((nfParamValue) => {
        const nfParamInputValue: InputNfParamValueInput = createNfParamValueInput({ text: nfParamValue?.textValue });
        nfParamInputValues.push(nfParamInputValue);
      });
      break;
    }
    case NfParamTypeEnum.Number:
      value?.forEach((nfParamValue) => {
        const nfParamInputValue: InputNfParamValueInput = createNfParamValueInput({
          numberValue: nfParamValue?.numberValue,
        });
        nfParamInputValues.push(nfParamInputValue);
      });
      break;
    case NfParamTypeEnum.Boolean:
      value?.forEach((nfParamValue) => {
        const nfParamInputValue: InputNfParamValueInput = createNfParamValueInput({
          booleanValue: nfParamValue?.booleanValue,
        });
        nfParamInputValues.push(nfParamInputValue);
      });
      break;

    case NfParamTypeEnum.File: {
      value?.forEach((nfParamValue) => {
        const nfParamInputValue: InputNfParamValueInput = createNfParamValueInput({ cfiId: nfParamValue.file.id });
        nfParamInputValues.push(nfParamInputValue);
      });
      break;
    }
    case NfParamTypeEnum.Genome: {
      value?.forEach((nfParamValue) => {
        const nfParamInputValue: InputNfParamValueInput = createNfParamValueInput({
          genome: {
            fileId: nfParamValue.file?.id,
            genomeId: nfParamValue.organism?.id,
          },
        });
        nfParamInputValues.push(nfParamInputValue);
      });
      break;
    }
    case NfParamTypeEnum.Region: {
      value?.forEach((nfParamValue) => {
        const nfParamInputValue: InputNfParamValueInput = createNfParamValueInput({
          region: {
            contigId: nfParamValue.region?.contig?.id,
            range: nfParamValue.region?.range,
          },
        });
        nfParamInputValues.push(nfParamInputValue);
      });
      break;
    }
  }

  const paramInput: InputNfParamInput = {
    ...workflowParam,
    values: nfParamInputValues,
  };

  return paramInput;
}

// TODO can we get rid of this constraint ? every prop should be optional
export function createNfParamValueInput(paramValueInput: Partial<InputNfParamValueInput>): InputNfParamValueInput {
  return {
    cfiId: null,
    genome: null,
    region: null,
    text: null,
    numberValue: null,
    booleanValue: null,
    ...paramValueInput,
  };
}

export function createNfParamValue(paramValueInput: Partial<NfParamValue>): NfParamValue {
  return {
    file: null,
    textValue: null,
    numberValue: null,
    booleanValue: null,
    region: null,
    organism: null,
    ...paramValueInput,
  };
}

export function isHiddenNfParamType(type: NfParamTypeInput) {
  return type.type === NfParamTypeEnum.OutputDir;
}
